import React, { memo, useCallback, useMemo } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  TextField,
  Box,
  CircularProgress,
  Typography,
  Avatar,
  Chip,
} from "@mui/material";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { api } from "../../api/Intercepter";
import { useSnackbar } from "notistack";
import PersonIcon from "@mui/icons-material/Person";

interface AddParticipationModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userInfo: {
    clientId: number;
    fullName: string;
    phoneNumber?: string;
    messageId?: number;
    callId?: number;
  };
  modalType: "message" | "call" | "bonus";
}

interface FormValues {
  prizeCode: string;
  prize: string;
  price: string;
}

const schema = yup.object().shape({
  prizeCode: yup
    .string()
    .nullable()
    .test("is-number", "Prize code must be a valid number", (value) => {
      if (!value) return true;
      return !isNaN(Number(value));
    }),
  prize: yup.string().required("Prize is required"),
  price: yup.string().required("Prize Cost is required"),
});

// Memoized Form Content Component
const FormContent = memo(
  ({ control, errors }: { control: any; errors: any }) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Controller
          name="prizeCode"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth error={!!errors.prizeCode} sx={{ mb: 1 }}>
              <TextField
                {...field}
                label="Prize Code (optional)"
                variant="outlined"
                error={!!errors.prizeCode}
                placeholder="e.g. 4025"
                type="text"
                fullWidth
                helperText={errors.prizeCode?.message}
              />
            </FormControl>
          )}
        />
        <Controller
          name="prize"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth error={!!errors.prize} sx={{ mb: 1 }}>
              <TextField
                {...field}
                label="Prize"
                variant="outlined"
                error={!!errors.prize}
                placeholder="e.g. رصيد, بسكت"
                fullWidth
                helperText={errors.prize?.message}
              />
            </FormControl>
          )}
        />
      </Box>
      <Controller
        name="price"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth error={!!errors.price} sx={{ mb: 1 }}>
            <TextField
              {...field}
              label="Prize Cost"
              variant="outlined"
              error={!!errors.price}
              placeholder="e.g. 5 $"
              type="number"
              fullWidth
              helperText={errors.price?.message}
            />
          </FormControl>
        )}
      />
    </Box>
  )
);

const AddParticipationModal: React.FC<AddParticipationModalProps> = ({
  open,
  onClose,
  onSuccess,
  userInfo,
  modalType,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = React.useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      prizeCode: "",
      prize: "",
      price: "",
    },
  });

  React.useEffect(() => {
    if (open) {
      reset({ prizeCode: "", prize: "", price: "" });
    }
  }, [open, reset]);

  const onSubmit = useCallback(
    async (data: FormValues) => {
      setIsLoading(true);
      try {
        await api.post("/participations", {
          clientId: userInfo.clientId,
          messageId:
            modalType === "message" ? userInfo.messageId || null : null,
          callId: modalType === "call" ? userInfo.callId || null : null,
          phoneNumber: userInfo.phoneNumber || "",
          prizeCode: Number(data.prizeCode),
          prize: data.prize,
          price: Number(data.price),
          participationType: modalType,
        });

        enqueueSnackbar("Participation created successfully", {
          variant: "success",
        });
        reset();
        onSuccess();
        onClose();
      } catch (error) {
        console.error("Error creating participation:", error);
        enqueueSnackbar("Failed to create participation", { variant: "error" });
      } finally {
        setIsLoading(false);
      }
    },
    [userInfo, modalType, enqueueSnackbar, reset, onSuccess, onClose]
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const modalTitle = useMemo(() => {
    const typeLabel =
      modalType === "message"
        ? "Message"
        : modalType === "call"
        ? "Call"
        : "Contact";
    return `Add Participation (${typeLabel})`;
  }, [modalType]);

  const userInfoDisplay = useMemo(
    () => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          backgroundColor: "#f8f9fa",
          borderRadius: 2,
          border: "1px solid #e9ecef",
        }}
      >
        <Avatar sx={{ bgcolor: "primary.main" }}>
          <PersonIcon />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={500}>
            {userInfo.fullName}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 0.5 }}>
            <Chip
              label={`Client ID: ${userInfo.clientId}`}
              size="small"
              color="primary"
              variant="outlined"
            />
            {userInfo.phoneNumber && (
              <Chip
                label={`Phone: ${userInfo.phoneNumber}`}
                size="small"
                color="secondary"
                variant="outlined"
              />
            )}
          </Box>
        </Box>
      </Box>
    ),
    [userInfo.fullName, userInfo.clientId, userInfo.phoneNumber]
  );

  const PrizeCodeField = useCallback(
    () => (
      <Controller
        name="prizeCode"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth error={!!errors.prizeCode} sx={{ mb: 1 }}>
            <TextField
              {...field}
              label="Prize Code (optional)"
              variant="outlined"
              error={!!errors.prizeCode}
              placeholder="e.g. 4025"
              type="text"
              fullWidth
              helperText={errors.prizeCode?.message}
            />
          </FormControl>
        )}
      />
    ),
    [control, errors.prizeCode]
  );

  const PrizeField = useCallback(
    () => (
      <Controller
        name="prize"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth error={!!errors.prize} sx={{ mb: 1 }}>
            <TextField
              {...field}
              label="Prize"
              variant="outlined"
              error={!!errors.prize}
              placeholder="e.g. رصيد, بسكت"
              fullWidth
              helperText={errors.prize?.message}
            />
          </FormControl>
        )}
      />
    ),
    [control, errors.prize]
  );

  const PriceField = useCallback(
    () => (
      <Controller
        name="price"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth error={!!errors.price} sx={{ mb: 1 }}>
            <TextField
              {...field}
              label="Prize Cost"
              variant="outlined"
              error={!!errors.price}
              placeholder="e.g. 5 $"
              type="number"
              fullWidth
              helperText={errors.price?.message}
            />
          </FormControl>
        )}
      />
    ),
    [control, errors.price]
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      keepMounted
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 16px rgba(0, 0, 0, 0.12)",
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h5" fontWeight={600}>
            {modalTitle}
          </Typography>
        </Box>
      </DialogTitle>
      <Box sx={{ px: 3, pb: 2 }}>{userInfoDisplay}</Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              <PrizeCodeField />
              <PrizeField />
            </Box>
            <PriceField />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              borderRadius: 2,
              background: "#3f51b5",
              boxShadow: "0 3px 5px 2px rgba(33, 150, 243, .3)",
              minWidth: 100,
            }}
          >
            {isLoading ? <CircularProgress size={24} /> : "Add Participation"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default memo(AddParticipationModal);
